/**
 * Stylesheet de-duplication — the wire format, and both ends of it.
 *
 * Deliberately free of React and of rrweb, for the same reason as
 * ./sidecarStreams.js: the writer runs in the app, the reader runs in the
 * viewer, and keeping the shared contract in one plain module means they agree
 * by construction rather than by two copies staying in sync.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * Both recorders run with `inlineStylesheet: true`, which is correct — the
 * EMR's stylesheets sit behind the customer's session and would 401 at replay
 * time. The cost is that rrweb re-serialises a sheet IN FULL every time it
 * appears: once per checkout full-snapshot, and again in every mutation whose
 * `adds` re-inserts a <style> node (EMR SPAs re-mount styled components
 * constantly).
 *
 * Measured on the largest replay in the bucket — 573 MB stored, ~3.1 GB
 * expanded — a six-chunk sample held 262 `_cssText` values totalling 114.5 MB,
 * drawn from 21 distinct stylesheets totalling 7.07 MB. 82% of the entire
 * replay was the same stylesheets over and over.
 *
 * ── The format ─────────────────────────────────────────────────────────────
 * The first copy of a sheet keeps its text and gains a marker; later copies are
 * replaced by a reference to it:
 *
 *   first:  "/*cpt-css-def:7*\/body{…}"
 *   later:  "/*cpt-css-ref:7*\/"
 *
 * A CSS comment, not a sentinel object, on purpose. The value stays a string,
 * so nothing downstream sees a changed shape; and a definition that is never
 * rehydrated is still valid CSS that renders correctly. Only repeats degrade,
 * and only to "unstyled".
 *
 * Ids are per replay and are never reset, so a chunk that fails to upload takes
 * the definitions it carried with it and later references to those sheets
 * cannot be resolved. That is a deliberate trade: re-defining periodically
 * would have cost most of the saving above, chunk loss already breaks the DOM
 * stream far worse than it breaks styling, and the viewer says so out loud
 * rather than rendering a mystery — see readCss().
 */

const DEF_PREFIX = '/*cpt-css-def:';
const REF_PREFIX = '/*cpt-css-ref:';
const MARKER_END = '*/';

/** Longest marker we could ever have written; bounds the indexOf below. */
const MAX_MARKER_LENGTH = REF_PREFIX.length + 20 + MARKER_END.length;

function parseMarker(value, prefix) {
  if (!value.startsWith(prefix)) return null;
  const end = value.indexOf(MARKER_END, prefix.length);
  if (end === -1 || end > MAX_MARKER_LENGTH) return null;
  const id = value.slice(prefix.length, end);
  if (!/^\d+$/.test(id)) return null;
  return { id, rest: value.slice(end + MARKER_END.length) };
}

/* ───────────────────────── the tree walk ───────────────────────── */

/**
 * Apply `fn` to every `_cssText` attribute reachable from one rrweb event,
 * replacing the value with whatever it returns.
 *
 * Handles the shapes that carry stylesheets, plus our own EMR batch which
 * nests more of them:
 *   type 2 (FullSnapshot)          → data.node, a nested tree
 *   type 3 (Mutation, source 0)    → data.adds[].node, more nested trees, and
 *                                    data.attributes[], a flat list — rrweb
 *                                    inlines a whole sheet there when a <link>
 *                                    finishes loading (attachLinkElement)
 *   type 5 (Custom, tag cpt-emr)   → data.payload.events[], each of the above
 *
 * Same shape and the same in-place mutation as masking.js's scrubEventAttributes,
 * and for the same reason: these events are large and this runs on every one.
 */
function mapEventCss(event, fn) {
  if (!event || typeof event !== 'object' || !event.data) return event;

  if (event.type === 2) {
    mapNodeCss(event.data.node, fn);
  } else if (event.type === 3 && event.data.source === 0) {
    if (Array.isArray(event.data.adds)) {
      for (const add of event.data.adds) mapNodeCss(add && add.node, fn);
    }
    if (Array.isArray(event.data.attributes)) {
      for (const mutation of event.data.attributes) mapAttributeCss(mutation, fn);
    }
  } else if (event.type === 5 && event.data.tag === 'cpt-emr') {
    const inner = event.data.payload && event.data.payload.events;
    if (Array.isArray(inner)) {
      for (const innerEvent of inner) mapEventCss(innerEvent, fn);
    }
  }

  return event;
}

function mapAttributeCss(holder, fn) {
  const attributes = holder && holder.attributes;
  if (attributes && typeof attributes === 'object' && typeof attributes._cssText === 'string') {
    attributes._cssText = fn(attributes._cssText);
  }
}

function mapNodeCss(node, fn) {
  if (!node || typeof node !== 'object') return;
  mapAttributeCss(node, fn);
  if (Array.isArray(node.childNodes)) {
    for (const child of node.childNodes) mapNodeCss(child, fn);
  }
}

/* ───────────────────────── capture side ───────────────────────── */

/**
 * Ceiling on the text the capture-side table keeps.
 *
 * The table pins one copy of every DISTINCT stylesheet for the life of the
 * replay, which is retention the recorder did not have before. That is a good
 * trade while sheets repeat — 21 of them stood in for 262 copies on the replay
 * this was built against. It stops being one on a page that rewrites a sheet
 * through CSSOM (emotion and styled-components append rules to a single
 * <style>), because every re-serialisation is a different string: nothing
 * matches, and the table would grow for hours.
 *
 * Past this, new sheets are simply not registered — they are written out in
 * full, exactly as they were before any of this existed. The de-duplication
 * degrades; nothing breaks.
 */
const MAX_TABLE_BYTES = 16 * 1024 * 1024;

/** One table per replay. Held by the recorder and cleared with it. */
export function createCssTable() {
  return { ids: new Map(), next: 1, bytes: 0 };
}

/**
 * Replace repeated stylesheets in one event with references, in place.
 *
 * Keyed on the full text rather than a fingerprint: a Map lookup hashes the
 * string once and V8 caches that hash on it, so the whole cost for a two-hour
 * replay is a fraction of a second — cheap enough that it is not worth the
 * paragraph a fingerprint would need to explain why its collisions are safe.
 */
export function dedupeEventCss(event, table) {
  if (!table) return event;
  return mapEventCss(event, (text) => {
    if (!text) return text;
    const existing = table.ids.get(text);
    if (existing !== undefined) return `${REF_PREFIX}${existing}${MARKER_END}`;
    if (table.bytes + text.length > MAX_TABLE_BYTES) return text;
    const id = table.next;
    table.next += 1;
    table.bytes += text.length;
    table.ids.set(text, id);
    return `${DEF_PREFIX}${id}${MARKER_END}${text}`;
  });
}

/* ───────────────────────── viewer side ───────────────────────── */

/** One index per replay being loaded. Threaded through every chunk in order. */
export function createCssIndex() {
  return { byId: new Map(), byText: new Map(), unresolved: 0 };
}

/**
 * Resolve references and intern stylesheet text in one event, in place.
 *
 * Does double duty, which is why the viewer needs no format check:
 *
 *   new captures  references are resolved back to the defining text.
 *   old captures  every replay already in the bucket predates the format above
 *                 and carries raw duplicates. Those are INTERNED — each unique
 *                 sheet is collapsed to one shared JS string, so the duplicates
 *                 become garbage instead of retained heap. That is where the
 *                 82% saving comes from on the replays we already have, without
 *                 re-recording anything.
 *
 * Chunks must be passed in timeline order so a definition is always seen before
 * the references to it; SessionReplayViewer already loads them that way.
 */
export function rehydrateEventCss(event, index) {
  if (!index) return event;
  return mapEventCss(event, (text) => readCss(text, index));
}

function readCss(text, index) {
  if (!text) return text;

  // A reference is the WHOLE value and nothing else. Matching on the prefix
  // alone would let a real stylesheet that happens to open with the marker be
  // read as a reference, throwing the rest of the sheet away. A definition
  // needs no such guard: we always write that marker ourselves, immediately
  // followed by the text it introduces.
  const ref = parseMarker(text, REF_PREFIX);
  if (ref && ref.rest === '') {
    const resolved = index.byId.get(ref.id);
    if (resolved !== undefined) return resolved;
    // The chunk carrying the definition never made it to S3. Say so in the
    // replay itself: a developer looking at an unstyled frame should be able to
    // tell a lost chunk from a page that genuinely had no styles.
    index.unresolved += 1;
    return `/* cpt-replay: stylesheet ${ref.id} is missing — the chunk that defined it was not uploaded */`;
  }

  const def = parseMarker(text, DEF_PREFIX);
  if (def) {
    const canonical = intern(def.rest, index);
    index.byId.set(def.id, canonical);
    return canonical;
  }

  return intern(text, index);
}

function intern(text, index) {
  const existing = index.byText.get(text);
  if (existing !== undefined) return existing;
  index.byText.set(text, text);
  return text;
}
